import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { WF } from "../constants/testIds2";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Buildings } from "@phosphor-icons/react";
import { Badge } from "../components/ui/badge";

const CURRENCIES = ["INR", "USD", "EUR", "GBP", "AED", "SGD"];

export default function Clients() {
  const { user, tenantId } = useAuth();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ code: "", name: "", primary_contact_email: "", billing_currency: "INR", address: "" });
  const [err, setErr] = useState("");
  const canManage = ["super_admin", "company_admin", "hr"].includes(user?.role);

  const load = () => {
    const params = user?.role === "super_admin" && tenantId ? { tenant_id: tenantId } : {};
    api.get("/clients", { params }).then(({ data }) => setRows(data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tenantId, user?.role]);

  const submit = async () => {
    setErr("");
    try {
      const payload = { ...form };
      if (user?.role === "super_admin" && tenantId) payload.tenant_id = tenantId;
      await api.post("/clients", payload);
      toast.success("Client added");
      setOpen(false); setForm({ code: "", name: "", primary_contact_email: "", billing_currency: "INR", address: "" });
      load();
    } catch (e) { setErr(formatApiError(e)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-emerald-700">Workforce</div>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">Clients</h1>
          <p className="text-sm text-zinc-500 mt-2">{rows.length} companies you deliver services to.</p>
        </div>
        {canManage && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="btn-zenith rounded-sm" data-testid={WF.clientCreateBtn}><Plus size={16} className="mr-1.5" /> New client</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-display text-2xl">Add client</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label className="text-xs">Code *</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className="rounded-sm font-mono-plex" data-testid="client-code-input" /></div>
                <div className="space-y-1"><Label className="text-xs">Currency</Label>
                  <Select value={form.billing_currency} onValueChange={(v) => setForm({ ...form, billing_currency: v })}>
                    <SelectTrigger className="rounded-sm" data-testid="client-currency-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-2 space-y-1"><Label className="text-xs">Company name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm" data-testid="client-name-input" /></div>
                <div className="col-span-2 space-y-1"><Label className="text-xs">Primary contact email</Label><Input type="email" value={form.primary_contact_email} onChange={(e) => setForm({ ...form, primary_contact_email: e.target.value })} className="rounded-sm" data-testid="client-email-input" /></div>
                <div className="col-span-2 space-y-1"><Label className="text-xs">Billing address</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="rounded-sm" data-testid="client-address-input" /></div>
              </div>
              {err && <div className="text-sm text-red-600 border border-red-200 bg-red-50 px-3 py-2 rounded-sm">{err}</div>}
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
                <Button onClick={submit} className="btn-zenith rounded-sm" data-testid={WF.clientSaveBtn}>Save</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid={WF.clientsList}>
        {rows.length === 0 && <div className="col-span-full text-center py-12 border border-zinc-200 bg-white text-sm text-zinc-500 rounded-sm">No clients yet.</div>}
        {rows.map((c) => (
          <div key={c.id} className="border border-zinc-200 rounded-sm p-5 bg-white transition-colors duration-150 hover:border-emerald-300">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-sm bg-emerald-700 grid place-items-center"><Buildings weight="duotone" size={20} className="text-white" /></div>
                <div>
                  <div className="font-display text-lg font-bold text-zinc-900">{c.name}</div>
                  <div className="text-xs font-mono-plex text-zinc-500">{c.code}</div>
                </div>
              </div>
              <Badge variant="outline" className="rounded-sm bg-zinc-50 border-zinc-300 text-zinc-800">{c.billing_currency}</Badge>
            </div>
            {c.primary_contact_email && <div className="mt-3 text-xs text-zinc-600">{c.primary_contact_email}</div>}
            {c.address && <div className="mt-1 text-xs text-zinc-500">{c.address}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}
